import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import { isDocument, isImage, listFiles, maxFilesFor, removeFile, signedLinks, uploadFile, validateFile } from './files.js';

const grid = { display: 'flex', flexWrap: 'wrap', gap: 10, margin: '8px 0' };
const tile = { width: 96, display: 'grid', gap: 4, fontSize: 12 };
const thumb = { width: 96, height: 96, objectFit: 'cover', borderRadius: 12, background: 'var(--surface-soft)', display: 'grid', placeItems: 'center', border: '1px solid var(--line)', cursor: 'pointer', padding: 0, overflow: 'hidden' };
const viewer = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 16 };
const acceptFor = { photos: 'image/jpeg,image/png,image/heic,image/heif,image/webp', documents: 'application/pdf', 'photos-and-documents': 'image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf' };

/**
 * Files attached to one item. Links expire after two minutes and are fetched again on
 * open. Props: scope, scopeId, title, kinds, canAdd, canRemoveAll, emptyText, hideWhenEmpty.
 */
export function AttachmentPanel({ scope, scopeId, title = 'Photos and documents', kinds = 'photos-and-documents', canAdd = false, canRemoveAll = false, emptyText = 'No files yet.', hideWhenEmpty = false }) {
  const { user } = useAuth() || {};
  const [rows, setRows] = useState([]);
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const load = useCallback(async () => {
    if (!supabase) { setLoading(false); return; }
    try {
      const list = await listFiles(supabase, scope, scopeId);
      const urls = await signedLinks(supabase, list);
      if (live.current) { setRows(list); setLinks(urls); setError(''); }
    } catch (cause) { if (live.current) setError(cause.message); }
    finally { if (live.current) setLoading(false); }
  }, [scope, scopeId]);
  useEffect(() => { void load(); }, [load]);

  const add = async (event) => {
    const picked = [...(event.target.files || [])];
    event.target.value = '';
    const room = maxFilesFor(scope) - rows.length;
    const problems = [];
    if (picked.length > room) problems.push(`Only ${Math.max(room, 0)} more files can be added.`);
    const files = picked.slice(0, Math.max(room, 0)).filter((file) => { const p = validateFile(file, kinds); if (p) problems.push(p); return !p; });
    for (const [index, file] of files.entries()) {
      if (live.current) setStatus(`Uploading ${index + 1} of ${files.length}…`);
      try { await uploadFile(supabase, { scope, scopeId, file }); }
      catch (cause) { problems.push(cause.message); }
    }
    if (!live.current) return;
    setStatus(''); setError([...new Set(problems)].join(' '));
    await load();
  };

  const open = async (row) => {
    const fresh = await signedLinks(supabase, [row]).catch(() => ({}));
    const link = fresh[row.storage_path] || links[row.storage_path];
    if (!link) { setError('That file is not available right now.'); return; }
    if (isDocument(row.mime_type)) window.open(link, '_blank', 'noopener,noreferrer');
    else setViewing(link);
  };

  const remove = async (row) => {
    if (!window.confirm('Remove this file? It will no longer be visible to anyone.')) return;
    try { await removeFile(supabase, row.id); await load(); } catch (cause) { setError(cause.message); }
  };

  if (hideWhenEmpty && !canAdd && (loading || !rows.length)) return null;
  return <div className="attachment-panel">
    {title ? <strong>{title}</strong> : null}
    {loading ? <p><small>Loading files…</small></p> : rows.length ? <div style={grid}>
      {rows.map((row, index) => {
        const doc = isDocument(row.mime_type);
        const name = row.caption || (doc ? `Document ${index + 1}` : `Photo ${index + 1}`);
        const pending = row.status === 'quarantined';
        const mine = user && (row.owner_user_id === user.id || canRemoveAll);
        return <div key={row.id} style={tile}>
          <button type="button" style={thumb} disabled={pending} onClick={() => void open(row)} aria-label={`Open ${name}`}>
            {!doc && isImage(row.mime_type) && links[row.storage_path] ? <img src={links[row.storage_path]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <span>{doc ? 'PDF' : 'Photo'}</span>}
          </button>
          {pending ? <small>Being checked</small> : doc ? <small>{name}</small> : null}
          {mine ? <button type="button" className="button button-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => void remove(row)}>Remove</button> : null}
        </div>;
      })}
    </div> : <p><small>{emptyText}</small></p>}
    {canAdd && supabase ? <label className="button button-secondary" style={{ display: 'inline-block', cursor: status ? 'wait' : 'pointer' }}>
      {kinds === 'documents' ? 'Add PDF' : kinds === 'photos' ? 'Add photos' : 'Add photos or PDFs'}
      <input type="file" multiple accept={acceptFor[kinds]} onChange={(e) => void add(e)} disabled={!!status || rows.length >= maxFilesFor(scope)} style={{ display: 'none' }}/>
    </label> : null}
    {status ? <p role="status"><small>{status}</small></p> : null}
    {error ? <p className="account-form-error" role="alert">{error}</p> : null}
    {viewing ? <div style={viewer} role="dialog" aria-label="Photo" onClick={() => setViewing(null)}>
      <img src={viewing} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }}/>
      <button type="button" className="button button-secondary" style={{ position: 'absolute', top: 16, right: 16 }} onClick={() => setViewing(null)}>Close</button>
    </div> : null}
  </div>;
}

/** Job photos for feed cards. Renders nothing when there are none. */
export function JobPhotos({ scope, jobId }) {
  return <AttachmentPanel scope={scope} scopeId={jobId} title="" kinds="photos" hideWhenEmpty/>;
}
